import {
  useCallback, useMemo,
} from 'react';
import { useDispatch } from 'react-redux';
import { getColorsFromPalette } from '~/_shared/utils/colors/color.palette';
import { useTranslation } from '~/_shared/utils/hooks';
import { notNullsy } from '~/_shared/utils/typeGuards';
import type { BoundaryGroupId } from '~/store/boundaries/boundaryIdentifier.type';
import { BoundaryTerritoryType } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroup.type';
import { activeBTGIdPerBoundaryGroupChanged } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.actionCreators';
import { useGetBoundaryTerritoryGroup } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.selectors';
import { useActiveBTGIdPerBoundaryGroup } from '~/store/mapSettings/toolsState/boundary/activeBTG.selectors';
import type { SortedBTGs } from '../boundaryTools.types';
import {
  type ActiveBoundaryFillInfoEntities, useGetBoundaryFillEntities,
} from './useGetBoundaryFillEntities';

export const useBTGFillData = ({
  boundaryGroupId,
  boundaryTerritoryGroups,
}: {
  boundaryGroupId: BoundaryGroupId;
  boundaryTerritoryGroups: SortedBTGs;
}) => {
  const dispatch = useDispatch();
  const [t] = useTranslation();
  const getBoundaryFillEntities = useGetBoundaryFillEntities();
  const { getBoundaryTerritoryGroupById } = useGetBoundaryTerritoryGroup();
  const activeBTGId = useActiveBTGIdPerBoundaryGroup(boundaryGroupId) ?? boundaryTerritoryGroups[1]?.boundaryTerritoryGroupId ?? boundaryTerritoryGroups[0]?.boundaryTerritoryGroupId;
  const activeBoundaryTerritoryGroup = getBoundaryTerritoryGroupById(activeBTGId) ?? boundaryTerritoryGroups[1] ?? boundaryTerritoryGroups[0];

  const hasMoreThanOneFills = boundaryTerritoryGroups.filter(notNullsy).length > 1;

  const noFillEntities: ActiveBoundaryFillInfoEntities = useMemo(() => ({
    mainEntity: t('BoundaryFill.Manual'),
    shortenedMainEntity: t('BoundaryFill.Manual'),
  }), [t]);

  const allBTGDropDownOptions = boundaryTerritoryGroups
    .map((btg, index) => {
      if (!btg) {
        return null;
      }
      const { background, foreground } = getColorsFromPalette(index);
      const boundaryFillEntities = getBoundaryFillEntities(btg);

      const isNoFill = !boundaryFillEntities;
      const entities = boundaryFillEntities ?? noFillEntities;

      return {
        fillOrderNumber: hasMoreThanOneFills ? index : null,
        backgroundColor: background,
        foregroundColor: foreground,
        boundaryTerritoryGroup: btg,
        entities,
        isNoFill,
      } as const;
    }).filter(notNullsy);

  const setActiveBTGId = useCallback((boundaryTerritoryGroupId: number) => {
    dispatch(activeBTGIdPerBoundaryGroupChanged(boundaryGroupId, boundaryTerritoryGroupId));
  }, [boundaryGroupId, dispatch]);

  const activeBTGOption = allBTGDropDownOptions.find(
    btgOption => btgOption.boundaryTerritoryGroup.boundaryTerritoryGroupId
      === activeBoundaryTerritoryGroup.boundaryTerritoryGroupId
  );
  const filteredBTGDropDownOptions = allBTGDropDownOptions
    .filter(btgOption => btgOption !== activeBTGOption);

  const allowFillChange = (activeBoundaryTerritoryGroup.settings.boundaryTerritoryType !== BoundaryTerritoryType.Manual) ||
    (filteredBTGDropDownOptions.length !== 0);

  const isOnlyNofillOption = allBTGDropDownOptions.length === 1 && allBTGDropDownOptions[0]?.isNoFill;

  return {
    activeBTGOption,
    activeBoundaryTerritoryGroup,
    allowFillChange,
    filteredBTGDropDownOptions,
    setActiveBTGId,
    isOnlyNofillOption,
  };
};
